import { m, type Message } from '@/i18n';
import { dependencyEndsAfter } from './clipped-border';
import { readModuleField } from './modules';
import { isLate, lateByDays, parseTimeValue, type TimeValue } from './time-value';
import { isClosed, vacancyFires } from './tags';

/**
 * Facts: authorless arithmetic over the log and the modules
 * (docs/HEALTH_SIGNALS.md §0/§2). A fact states, it never judges: no colour, no
 * grade, no routing decision lives here. Facts are computed at read time and
 * never stored (CONTRACT_SPINE §5.4 discipline); they clear because the world
 * changes (activity resumes, a date moves, a seat fills, the item settles) -
 * never because someone dismissed them (there is no dismissal; HEALTH_SIGNALS
 * §10 killed it).
 *
 * **No declaration suppresses a fact** (HS-B4): an on-hold item's "quiet 30
 * days" stays true and visible. What declared rest changes is which RULES fire -
 * a rule may carry `when` conditions in its own pattern - never the fact itself.
 *
 * **Every fact here is unconditional** (docs/THRESHOLDS.md §1, 2026-07-26). No
 * fact's existence depends on a number somebody would argue about; the
 * thresholds moved out to the flow's rules, and the surfaces decide what a fact
 * with no number is allowed to say (§6's health/metadata cut).
 *
 * Pure: the read layer gathers the inputs (last event, schedule module data,
 * seat state, dependent counts) and injects `now`; this file only decides.
 * Each variant carries the parameters its provenance line needs.
 */
export type Fact =
  /** Decay (computed): quiet-for-N-days over the event stream. `lastEventAt`
   *  is the epoch-ms the silence was computed against - the fact's instance
   *  ANCHOR (domain/qualified-facts.ts): any new event starts a new silence,
   *  hence a new instance (HS-G3). */
  | { kind: 'quiet'; quietDays: number; lastEventAt: number }
  /** Schedule (computed): the declared end interval has fully passed. The
   *  declared end VALUE is the instance anchor. */
  | { kind: 'end_passed'; end: TimeValue; lateDays: number }
  /** Schedule (computed): the declared start passed while the item is not
   *  started. The declared start value is the instance anchor. */
  | { kind: 'start_passed'; start: TimeValue; lateDays: number }
  /** Structure (event-mirrored): no held requester seat - nobody owns the
   *  demand. `seatEventSeq` is the log seq of the seat event that EMPTIED the
   *  seat (birth is never vacant, so it always exists) - the vacancy episode's
   *  instance anchor; null when the caller's gather did not supply it (the
   *  fact still states; it just carries no citable identity). */
  | {
      kind: 'no_requester';
      contributorHeld: boolean;
      dependentCount: number;
      seatEventSeq: number | null;
    }
  /** Structure (event-mirrored): no held contributor seat - needs doing.
   *  `seatEventSeq` is the log seq of the event that emptied (or, at birth,
   *  left open) the contributor seat - the episode's instance anchor
   *  (review R3: no statement kind is quietly uncitable); null when the
   *  caller's gather did not supply it. */
  | { kind: 'needs_doing'; seatEventSeq: number | null }
  /** Structure (event-mirrored): N items depend on this one. `edges` is the
   *  ACTIVE dependency edge-id set, sorted - the episode's instance anchor
   *  (review R3: any edge added or removed is a new instance). */
  | { kind: 'dependents'; count: number; edges: string[] }
  /** Pressure (computed, MACHINE.md §2/§3 - the pressure law's missing signal
   *  direction): N READY dependents wait on this item. "Ready" = a live
   *  dependent whose OTHER preconditions are all met, so THIS item is the last
   *  thing standing between it and progress (`readyDependentEdges`). The digest
   *  relays upstream weather DOWN to dependents; back-pressure is the REVERSE
   *  flow - urgency emitted UP by the needers, computed from the same edges.
   *  `edges` is the sorted ready-dependent edge-id set - the count AND the
   *  instance anchor (a dependent becoming ready or unready mints a new
   *  instance, HS-G3). EXIT: it clears when the ready dependents unblock (this
   *  item settles, so it produces no facts) or stop waiting (they close, their
   *  edge drops, or they gain another blocker - the set shrinks to empty). */
  | { kind: 'back_pressure'; readyCount: number; edges: string[] }
  /** Schedule, edge-borne (docs/HEALTH_SIGNALS_V2.md §9): a dependency's
   *  declared end lands after this item's own declared end - two promises
   *  joined by an edge that cannot both hold, derivable no matter how often
   *  either date is renegotiated (HS-K2: laundering does not hide the
   *  exposure). Computed only when BOTH ends are declared: an undeclared own
   *  end makes no promise to conflict with (only the crossing event speaks),
   *  and an undeclared dependency end states nothing. The instance identity is
   *  the EDGE, this item's OWN end and the EPISODE (amended 2026-10-05): the
   *  dependency's end decides only whether the conflict exists, never which
   *  instance it is, because the dependency may be an item the dependent's
   *  viewers cannot open, and an identity that moved with its end would tell
   *  them every move (a qualification ending, a note dropping off the End
   *  field). A move of the own end is a new instance, fresh review (HS-K1,
   *  HS-G3); a move of the dependency's end that keeps it after is the same
   *  one; a conflict that ends and comes back is a new one (`since`). The dependency's
   *  raw end value does NOT ride the fact - display goes through the gated
   *  `scheduleConflictDetails` read (src/read/facts.ts), keyed by the edge.
   *  Nor does the dependency's id (2026-10-05): the fact names its edge alone,
   *  so a dependency the viewer sees only as its stub is named nowhere but on
   *  its relationship row. `ownEnd` is the subject's own declared value (its
   *  register may always show it). */
  | {
      kind: 'schedule_conflict';
      ownEnd: TimeValue;
      /** The `blocks` edge it is computed over: the relationship row the
       *  statement renders on, whether or not the viewer can see the far end
       *  (the edge's id names nothing of it). */
      edgeId: string;
      /** The log position where this conflict episode began
       *  (`conflictEpisodeStart`, src/domain/clipped-border.ts): part of the
       *  instance identity, on the server only. */
      since: number;
    }
  /** Divergence (computed, MACHINE.md §2 - the uncoupled clocks earn their
   *  keep): declared ACTIVE while the scoped work clock is silent past the
   *  (tempo-relative) threshold. THE reconciliation target - the flag is not
   *  erased and the quiet fact is not suppressed; the contradiction between the
   *  declaration and the trace is itself the signal (§4: declaring active buys
   *  no invisibility, it makes an idle item MORE visible). `lastEventAt` is the
   *  silence anchor (same as `quiet`). EXIT: work resumes (any act resets the
   *  clock) or the active flag is withdrawn / the item closes. */
  | { kind: 'committed_but_inert'; quietDays: number; lastEventAt: number }
  /** Divergence (computed, MACHINE.md §2): work ticking, intention stale -
   *  running on old intent (drift risk). The item is being worked (recent acts)
   *  but its definition layer (title/definition/dates/priority/scope) has not
   *  been re-tended past the (tempo-relative) horizon. `lastIntentionAt` is the
   *  intention-clock anchor. EXIT: the goal is re-tended (a definition-layer
   *  act) or the work stops moving. */
  | { kind: 'running_on_old_intent'; intentionQuietDays: number; lastIntentionAt: number }
  /** Divergence (computed, MACHINE.md §2): the responsible seat's attention is
   *  silent while exposure grows - ready dependents wait yet the held
   *  contributor has not acted past the (tempo-relative) threshold.
   *  `lastAttentionAt` is the attention-clock anchor (the contributor's last
   *  act, or the item's birth if never). EXIT: the responsible seat acts, the
   *  exposure clears (dependents unblock / stop waiting), or the seat empties. */
  | {
      kind: 'unattended_risk';
      attentionQuietDays: number;
      readyCount: number;
      lastAttentionAt: number;
    }
  /** Condition (docs/THRESHOLDS.md §2, "Conditions", 2026-10-14): this member
   *  of flow `flowId` carries `tag` (the rule's tag, folded) or a tag under it.
   *  A fact with a parameter the flow's rule supplies, so it is computed only
   *  for the conditions a current rule names (`src/read/condition-facts.ts`),
   *  never on the register. `since` is the episode: the later of the member's
   *  join and the earliest application of a matching tag (epoch ms). */
  | { kind: 'tagged'; flowId: string; tag: string; since: number }
  /** Condition (THRESHOLDS §2, 2026-10-14): this member of flow `flowId`
   *  stands in the stored stage `stage`. `stageLabel` is display only (the
   *  flow's word for the stage); `since` is the log position of the act that
   *  put the member there, the episode. */
  | { kind: 'in_stage'; flowId: string; stage: string; stageLabel: string; since: number };

export type FactKind = Fact['kind'];

/** The two CONDITION kinds (docs/THRESHOLDS.md §2, "Conditions"): facts whose
 *  parameter a flow's rule supplies. They are never on the register on their
 *  own, never relayed, and never graded by a lens; only a flow's rule names
 *  them, and only for its own members. */
export type ConditionFact = Extract<Fact, { kind: 'tagged' | 'in_stage' }>;
export type ConditionFactKind = ConditionFact['kind'];

export const CONDITION_FACT_KINDS: ReadonlySet<FactKind> = new Set<FactKind>([
  'tagged',
  'in_stage',
]);

export function isConditionFact(fact: Fact): fact is ConditionFact {
  return fact.kind === 'tagged' || fact.kind === 'in_stage';
}

/**
 * The facts that measure a silence **nobody put a number on**
 * (docs/THRESHOLDS.md §6). Each is true from day one now - the arithmetic is -
 * but "9 days of silence" says nothing until a human supplies what silence may
 * cost: a declared cadence on the item, or a flow's rule. Until then they are
 * METADATA ("last updated 2 days ago"), not health, and no surface states them
 * as a signal.
 *
 * The schedule facts are deliberately NOT in the set: `end_passed` measures
 * against a date its author declared, so the number is already there.
 */
export const FACTS_NEEDING_A_NUMBER: ReadonlySet<FactKind> = new Set<FactKind>([
  'quiet',
  'committed_but_inert',
  'running_on_old_intent',
  'unattended_risk',
]);

/**
 * A fact's OWN measure in days, or null when it measures no duration (a seat is
 * empty or it is not). This is what a rule's `thresholdDays` compares against
 * (`domain/fact-rules.ts`) and what the metadata line reads.
 */
export function factAgeDays(fact: Fact): number | null {
  switch (fact.kind) {
    case 'quiet':
    case 'committed_but_inert':
      return fact.quietDays;
    case 'end_passed':
    case 'start_passed':
      return fact.lateDays;
    case 'running_on_old_intent':
      return fact.intentionQuietDays;
    case 'unattended_risk':
      return fact.attentionQuietDays;
    default:
      return null;
  }
}

/**
 * §6's THIRD DOOR: the item numbered the silence itself. A declared cadence
 * ("this runs weekly", `domain/cadence.ts`) is a human supplying exactly the
 * value `quiet` lacks, on the item, without touching a flow - so past one
 * missed beat the work-clock silences become health (register statements,
 * adoptable), still UNCOLOURED: a cadence supplies a number, never a grade or
 * a word (THRESHOLDS §6: presence and colour are two separate questions).
 *
 * Scoped to the work clock on purpose: a cadence says how often the work beats,
 * which is nothing about when a goal was last tended (`running_on_old_intent`)
 * or how long a seat may look away (`unattended_risk`).
 */
export function cadenceNumbers(fact: Fact, cadenceBeatDays: number | null): boolean {
  if (cadenceBeatDays === null || cadenceBeatDays <= 0) return false;
  if (fact.kind !== 'quiet' && fact.kind !== 'committed_but_inert') return false;
  return fact.quietDays > cadenceBeatDays;
}

const DAY_MS = 86_400_000;

/** The per-item inputs the read layer gathers (facts are never queried per item). */
export interface FactInputs {
  /** The lifecycle atoms (docs/CLOSED_AXIS.md, three atoms): facts gate on
   *  the REAL closure (the closed atom) and read not-started/in-motion off
   *  the delivered/active flags - never the phase words. */
  closed: boolean;
  delivered: boolean;
  /** The DECLARED activity flag (MACHINE.md §1.II, dimension 6): gates the
   *  not-started reading and feeds committed-but-inert (active + quiet). */
  active: boolean;
  /** Epoch ms of the item's last log event (callers fall back to createdAt). */
  lastEventAt: number;
  /** The item's typed-module blob (schedule dates are read from it). */
  moduleData: Record<string, unknown>;
  /** Whether a requester seat is HELD (open/offered do not count - CONTRACT_SPINE §2). */
  requesterHeld: boolean;
  /** Whether a contributor seat is HELD. */
  contributorHeld: boolean;
  /** The ACTIVE dependency edges pointing here (lateral `blocks`, this item as
   *  the blocker): the edge ids, in any order - the dependents fact's count
   *  AND its instance anchor (sorted here, review R3). */
  dependentEdgeIds: string[];
  /** The log seq of the event that emptied the requester seat (gathered only
   *  for vacant items; feeds the vacancy fact's instance anchor). */
  requesterEmptiedSeq?: number;
  /** The log seq of the event that emptied (or, at birth, left open) the
   *  contributor seat - the needs-doing episode's instance anchor. */
  contributorEmptiedSeq?: number;
  /** The item's ACTIVE dependencies (lateral `blocks`, this item as the
   *  dependent): the far item's id and module blob, so the schedule-conflict
   *  arithmetic reads both declared ends with the same parser (v2 §9), and
   *  where a current conflict over the edge began (`conflictEpisodeStart`,
   *  read with the log; 0 for an edge with no conflict). */
  dependencies?: {
    itemId: string;
    edgeId: string;
    moduleData: Record<string, unknown>;
    conflictSince: number;
  }[];
  /** The READY-dependent edge-id set the read pre-computed with
   *  `readyDependentEdges` (the pressure law's graph arithmetic spans several
   *  items, so it is resolved in the read, not here): the back-pressure fact's
   *  edges, anchor and count. For a viewer's read it is the set they may know
   *  (2026-10-05): dependents they can open, ready by each one's other
   *  blockers counted by result (a stub blocker's result is the stub's).
   *  Absent/empty = no ready dependents = no back-pressure. */
  readyDependentEdgeIds?: string[];
  /** Epoch ms of the last INTENTION-clock act (MACHINE.md §2): the last change
   *  to the definition layer (title/definition/dates/priority/scope edges).
   *  Feeds running-on-old-intent. Absent = no intention clock (never checked). */
  lastIntentionChangeAt?: number;
  /** Epoch ms of the last ATTENTION-clock act by a held contributor
   *  (MACHINE.md §2): the responsible seat's last act, or the item's birth if it
   *  never acted. Feeds unattended-risk. Absent = no responsible attention to
   *  measure (no held contributor). */
  lastResponsibleActAt?: number;
}

/** One dependent's readiness, as gathered per its blocking edge (the input to
 *  the pure pressure predicate below). */
export interface DependentReadiness {
  /** The `blocks` edge id (dependent -> this item): a back-pressure anchor member. */
  edgeId: string;
  /** Whether the DEPENDENT item is itself closed - a closed dependent waits on
   *  nothing, so it exerts no pressure. */
  dependentClosed: boolean;
  /** How many of the dependent's OTHER blockers (≠ this item) are still
   *  unresolved - anything else in its way means this item is not the last thing
   *  it waits on. */
  unresolvedOtherBlockers: number;
}

/**
 * The READY subset of an item's dependents (MACHINE.md §3, the pressure law):
 * a dependent presses only when it is itself live AND nothing ELSE is in its
 * way, so this item is the last thing standing between it and progress. Returns
 * the sorted ready edge-id set - the back-pressure fact's count and its instance
 * anchor. Pure; the read gathers the readiness inputs (dependent liveness, other
 * blockers' resolution) across the edge graph and calls this.
 */
export function readyDependentEdges(deps: readonly DependentReadiness[]): string[] {
  return deps
    .filter((d) => !d.dependentClosed && d.unresolvedOtherBlockers === 0)
    .map((d) => d.edgeId)
    .sort();
}

function scheduleDate(moduleData: Record<string, unknown>, field: string): TimeValue | null {
  const raw = readModuleField(moduleData, 'schedule', field);
  if (typeof raw !== 'string' || raw.length === 0) return null;
  const parsed = parseTimeValue(raw);
  return parsed.ok ? parsed.value : null;
}

/**
 * Compute an item's facts. Concluded contracts (closed or cancelled) are
 * conclusions, not silences or vacancies - a fact would nag a closed book, so
 * none are produced (the "cancel in truth" reaction clears the register by
 * design; settlement retires concern facts, docs/CLOSED_AXIS.md). A
 * delivered-but-open item keeps its facts: the claim alone concludes nothing.
 */
export function factsFor(inputs: FactInputs, now: number): Fact[] {
  const facts: Fact[] = [];
  if (isClosed(inputs)) return facts;

  // Decay: unconditional arithmetic, and now unconditional in the literal sense
  // (docs/THRESHOLDS.md §1). The fact used to exist only past a platform
  // constant, which made its own EXISTENCE a judgment authored by nobody. It is
  // true from day one because the arithmetic is true from day one; whether 9
  // days of silence MEANS anything is a rule's to say, and a surface states it
  // only once somebody supplied the number (§6).
  const quietDays = Math.floor((now - inputs.lastEventAt) / DAY_MS);
  facts.push({ kind: 'quiet', quietDays, lastEventAt: inputs.lastEventAt });

  // Schedule: read off the declared dates; anchors without an absolute value
  // never resolve here (recorded, not auto-resolved - the board item "Known
  // gaps (July): modules and unbuilt ideas").
  const end = scheduleDate(inputs.moduleData, 'endTime');
  if (end && isLate(end, now)) {
    facts.push({ kind: 'end_passed', end, lateDays: lateByDays(end, now) });
  }
  const start = scheduleDate(inputs.moduleData, 'startTime');
  // Not started: no claim stands and nobody declares work (the old `phase ===
  // 'open'` reading, spoken in atoms; closure is already gated above).
  if (start && !inputs.delivered && !inputs.active && isLate(start, now)) {
    facts.push({ kind: 'start_passed', start, lateDays: lateByDays(start, now) });
  }

  // Structure: mirrored seat/edge state. A personal item (its person holds the
  // requester seat) is never vacant by construction (HS-C3).
  const dependentCount = inputs.dependentEdgeIds.length;
  if (!inputs.requesterHeld) {
    facts.push({
      kind: 'no_requester',
      contributorHeld: inputs.contributorHeld,
      dependentCount,
      seatEventSeq: inputs.requesterEmptiedSeq ?? null,
    });
  }
  if (!inputs.contributorHeld) {
    facts.push({ kind: 'needs_doing', seatEventSeq: inputs.contributorEmptiedSeq ?? null });
  }
  if (dependentCount > 0) {
    facts.push({
      kind: 'dependents',
      count: dependentCount,
      edges: [...inputs.dependentEdgeIds].sort(),
    });
  }

  // Pressure (§2/§3): the READY subset of those dependents - urgency emitted UP
  // by the needers. Distinct from `dependents` (exposure): back-pressure fires
  // only where a dependent is the-last-thing-waiting, so it states who this item
  // is stopping RIGHT NOW. The read pre-computed the ready edge set.
  const readyEdges = inputs.readyDependentEdgeIds ?? [];
  const readyCount = readyEdges.length;
  if (readyCount > 0) {
    facts.push({
      kind: 'back_pressure',
      readyCount,
      edges: [...readyEdges].sort(),
    });
  }

  // Schedule, edge-borne (v2 §9): the one unambiguous comparison - a
  // dependency's declared end after the own declared end, both declared.
  // Uncoloured like every fact; adoptable (-> Delayed / At risk) and, being
  // edge-borne, containable ("we have slack") on the dependent's own register.
  // The dependency's raw end value stays OUT of the produced fact (the edge
  // and the own end are the identity; the gated read is the display path).
  // The comparison is the clipped border's (src/domain/clipped-border.ts): for
  // a dependency the viewer cannot see, this fact IS its `ends_after` report.
  if (end) {
    for (const dep of inputs.dependencies ?? []) {
      const depEnd = scheduleDate(dep.moduleData, 'endTime');
      if (depEnd && dependencyEndsAfter(depEnd, end)) {
        facts.push({
          kind: 'schedule_conflict',
          ownEnd: end,
          edgeId: dep.edgeId,
          since: dep.conflictSince,
        });
      }
    }
  }

  // Divergences (MACHINE.md §2): where the uncoupled clocks disagree. Each is
  // additional to the raw traces it reads (the flag/quiet fact are not
  // suppressed, §4) - the disagreement is the signal.

  // Each divergence lost the platform threshold it used to lean on
  // (docs/THRESHOLDS.md §6): what remains is the comparison itself, and how
  // long is too long is a rule's to say.

  // committed-but-inert: declared active, and this is how long the work clock
  // has been silent. THE reconciliation target - declaring active on an idle
  // item makes it MORE visible, not less (§4).
  if (inputs.active) {
    facts.push({ kind: 'committed_but_inert', quietDays, lastEventAt: inputs.lastEventAt });
  }

  // running-on-old-intent: the group keeps moving but nobody has re-tended the
  // goal (drift risk). The "still moving" half is now a COMPARISON of the two
  // clocks rather than a constant: the work moved after the goal was last
  // tended. That is arithmetic, so it survives the threshold retirement intact.
  if (
    inputs.lastIntentionChangeAt !== undefined &&
    inputs.lastIntentionChangeAt < inputs.lastEventAt
  ) {
    facts.push({
      kind: 'running_on_old_intent',
      intentionQuietDays: Math.floor((now - inputs.lastIntentionChangeAt) / DAY_MS),
      lastIntentionAt: inputs.lastIntentionChangeAt,
    });
  }

  // unattended-risk: ready dependents are waiting (exposure) on a held seat,
  // which last acted this long ago. The exposure is structural; the silence is
  // the number a rule grades.
  if (inputs.contributorHeld && readyCount > 0 && inputs.lastResponsibleActAt !== undefined) {
    facts.push({
      kind: 'unattended_risk',
      attentionQuietDays: Math.floor((now - inputs.lastResponsibleActAt) / DAY_MS),
      readyCount,
      lastAttentionAt: inputs.lastResponsibleActAt,
    });
  }

  return facts;
}

/** `vacancyFires` (domain/tags.ts, the one shared condition) read off a
 *  produced fact list - the register-side spelling. */
export function vacancySignals(facts: Fact[]): boolean {
  const vacancy = facts.find((f) => f.kind === 'no_requester');
  return (
    vacancy !== undefined &&
    vacancyFires({
      requesterHeld: false,
      contributorHeld: vacancy.contributorHeld,
      dependentCount: vacancy.dependentCount,
    })
  );
}

/**
 * Which facts threaten a promise across a dependency edge (§4 routing:
 * "promise-threatening facts cross the dependency edge"). Decided here so the
 * notification layer and the item surfaces speak one vocabulary.
 *
 * **Only NUMBERED facts travel** (docs/THRESHOLDS.md §5a). A broken date promise
 * and a vacant requester seat cross unconditionally: both are already authored
 * (a declared date; a seat that is empty or is not). A silence crosses only once
 * a rule has GRADED it on the source item - nobody has said the silence threatens
 * a promise until somebody put a number on it, and an unconditional `quiet` would
 * otherwise reach every dependent's register from the day the item was created.
 * `gradedKinds` is the source item's fired-rule kinds, gathered by the caller.
 */
export function threatensDependents(fact: Fact, gradedKinds?: ReadonlySet<FactKind>): boolean {
  if (fact.kind === 'end_passed' || fact.kind === 'no_requester') return true;
  return FACTS_NEEDING_A_NUMBER.has(fact.kind) && (gradedKinds?.has(fact.kind) ?? false);
}

// --- The display grammar (shared by every surface, so provenance is uniform) ---

/**
 * A declared day inside a statement's line (Leon, 2026-07-26: dates in the
 * user's format). The domain cannot know how a date looks - that depends on the
 * reader's locale, which no server render knows - so a day rides the line as
 * this descriptor: formatted plain it is the canonical ISO day, and the
 * statement label (`src/components/statement-label.tsx`) renders it as a date
 * in the reader's locale, in mono.
 */
export function dayArg(day: string): Message {
  return m('facts.day', { day });
}

/** Whether an argument is a day (`dayArg`). */
export function isDayArg(value: unknown): value is Message & { args: { day: string } } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { key?: unknown }).key === 'facts.day' &&
    typeof (value as { args?: { day?: unknown } }).args?.day === 'string'
  );
}

/**
 * A fact's line, as a descriptor the surface formats. Leads with the
 * CONSEQUENCE and names the declared value it measured against ("6 days late,
 * end date 01.06.2026"): the reading anyone wants first is how far off it is,
 * and the value that makes it true follows as evidence. A declared day is a
 * `dayArg`.
 *
 * `dependencyEnd` is the far side of a schedule conflict, supplied ONLY when
 * the gated read admitted it (`scheduleConflictDetails`). Absent, the line
 * keeps HS-K5's clipped form - a foreign declared date is content, and content
 * never leaks through a fact.
 */
export function factLine(fact: Fact, dependencyEnd?: string): Message {
  switch (fact.kind) {
    case 'quiet':
      return m('facts.line.quiet', { count: fact.quietDays });
    case 'end_passed':
      return m('facts.line.endPassed', { count: fact.lateDays, end: dayArg(fact.end.canonical) });
    case 'start_passed':
      return m('facts.line.startPassed', { start: dayArg(fact.start.canonical) });
    case 'no_requester':
      return m('facts.line.noRequester');
    case 'needs_doing':
      return m('facts.line.needsDoing');
    case 'dependents':
      return m('facts.line.dependents', { count: fact.count });
    case 'back_pressure':
      return m('facts.line.backPressure', { count: fact.readyCount });
    case 'schedule_conflict':
      return dependencyEnd === undefined
        ? m('facts.line.scheduleConflict', { end: dayArg(fact.ownEnd.canonical) })
        : m('facts.line.scheduleConflictBoth', {
            arrives: dayArg(dependencyEnd),
            end: dayArg(fact.ownEnd.canonical),
          });
    case 'committed_but_inert':
      return m('facts.line.committedButInert', { count: fact.quietDays });
    // No "Working" claim (amended with docs/THRESHOLDS.md): the fact fires on
    // the CLOCK COMPARISON (the goal predates the current work), which is not
    // the same as asserting the work is moving right now. It states the one
    // thing it knows.
    case 'running_on_old_intent':
      return m('facts.line.runningOnOldIntent', { count: fact.intentionQuietDays });
    case 'unattended_risk':
      return m('facts.line.unattendedRisk', { count: fact.readyCount });
    case 'tagged':
      return m('facts.line.tagged', { tag: fact.tag });
    case 'in_stage':
      return m('facts.line.inStage', { stage: fact.stageLabel });
  }
}

/**
 * The one-line provenance chain (HS-F2), for the statements that HAVE one.
 *
 * Null for a plain fact, and that is the point (Leon, 2026-07-26: "just state
 * the facts"). HS-F2 exists so nothing unauthored can masquerade as judgment -
 * the chain it demands is the chain of AUTHORSHIP, and a fact has no author.
 * "computed from the log" was that absence rendered as words: a second line
 * per statement, restating the input the label already names ("End 2026-06-01,
 * 6 days late" is visibly from the schedule).
 *
 * So the arithmetic's boilerplate is gone and the label absorbed the one thing
 * it carried - the lateness. What keeps its chain is everything with a real
 * author to name: a declaration ("declared by Ari"), an adoption ("adopted by
 * Ari at warning"), and a relayed entry's route. Callers render nothing for
 * null.
 */
export function factProvenance(fact: Fact): Message | null {
  switch (fact.kind) {
    // Mirrored from a seat rather than computed - which seat is not obvious
    // from the words, so the chain still earns its line.
    case 'no_requester':
    case 'needs_doing':
      return m('facts.provenance.seats');
    default:
      return null;
  }
}
